import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { LogIn } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useLogin, useSSOLogin } from "@/hooks/useAuth"
import { ApiError } from "@/lib/api"

export default function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [showAdmin, setShowAdmin] = useState(false)
  const [checkingSSO, setCheckingSSO] = useState(true)
  const login = useLogin()
  const sso = useSSOLogin()
  const nav = useNavigate()
  const autoTried = useRef(false)

  // Seamless SSO: if the user is already signed into gn-cloud, the shared
  // SESSION cookie lets us log them in without any interaction.
  useEffect(() => {
    if (autoTried.current) return
    autoTried.current = true
    sso
      .mutateAsync()
      .then(() => nav("/", { replace: true }))
      .catch(() => setCheckingSSO(false)) // no gn-cloud session — show options
  }, [sso, nav])

  const doSSO = async () => {
    setErr(null)
    try {
      await sso.mutateAsync()
      nav("/", { replace: true })
    } catch (e) {
      setErr(
        e instanceof ApiError && e.code === "NO_SSO_SESSION"
          ? "gn-cloud에 먼저 로그인한 뒤 다시 시도하세요."
          : e instanceof ApiError
            ? e.message
            : "SSO 로그인 실패",
      )
    }
  }

  if (checkingSSO) {
    return (
      <div className="flex h-full items-center justify-center bg-background px-4">
        <p className="text-sm text-muted-foreground">gn-cloud 세션 확인 중…</p>
      </div>
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-xl bg-primary text-base font-extrabold text-primary-foreground">
            오논
          </div>
          <CardTitle className="text-xl">오논 — 오늘의 논문</CardTitle>
          <CardDescription>gn-cloud 계정으로 로그인하세요.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button className="w-full" onClick={doSSO} disabled={sso.isPending}>
            <LogIn className="h-4 w-4" />
            {sso.isPending ? "로그인 중…" : "gn-cloud 계정으로 로그인"}
          </Button>

          {err && (
            <p className="text-sm text-destructive" role="alert">
              {err}
            </p>
          )}

          {!showAdmin ? (
            <button
              type="button"
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowAdmin(true)}
            >
              관리자 로그인
            </button>
          ) : (
            <form
              className="space-y-3 border-t border-border pt-4"
              onSubmit={async (e) => {
                e.preventDefault()
                setErr(null)
                try {
                  await login.mutateAsync({ email, password })
                  nav("/", { replace: true })
                } catch (e) {
                  setErr(e instanceof ApiError ? e.message : "로그인 실패")
                }
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="email">이메일</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">비밀번호</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="w-full"
                disabled={login.isPending}
              >
                {login.isPending ? "로그인 중…" : "관리자 로그인"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
